import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import CustomPaginator from '../../shared/components/paginator/CustomPaginator';
import SelectStatus from '../../shared/components/selectStatus/SelectStatus';
import InputSearch from '../../shared/components/inputSearch/InputSearch';
import ButtonFilter from '../../shared/components/buttonFilter/ButtonFilter';
import ContractsTable from './ContractsTable';
import { TYPE_OPTIONS, STATUS_OPTIONS, secondaryButton } from './contractLabels';
import { searchContracts, runExpirations, errorMessage } from '../../services/contracts/contractsService';
import { getSensors } from '../../services/sensors/sensorsService';

const ITEMS_PER_PAGE = 15;
const EXPIRING_OPTIONS = [
    { label: 'Vencen en 7 días', value: 7 },
    { label: 'Vencen en 15 días', value: 15 },
    { label: 'Vencen en 30 días', value: 30 },
];

// Vista diaria del administrador: qué vence, qué está suspendido y qué está prestado
const ContractsView = () => {
    const { register, handleSubmit } = useForm({ defaultValues: { status: 'ACTIVE' } });
    const [filters, setFilters] = useState({ status: 'ACTIVE' });
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ content: [], totalPages: 1 });
    const [sensors, setSensors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v != null));
            const response = await searchContracts(page, ITEMS_PER_PAGE, params);
            setData(response);
            if (response.totalPages > 0 && page > response.totalPages) setPage(response.totalPages);
        } catch (e) {
            setError(errorMessage(e, 'Error obteniendo los contratos'));
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        getSensors(1, 1000).then((r) => setSensors(r.content)).catch((e) => console.error(e));
    }, []);

    const onFilter = handleSubmit((values) => {
        setPage(1);
        setFilters(values);
    });

    const handleRunExpirations = async () => {
        try {
            const result = await runExpirations();
            alert(result.changed === 0
                ? 'No había contratos por vencer o suspender.'
                : `Se actualizaron ${result.changed} contrato(s).`);
            load();
        } catch (e) {
            alert(errorMessage(e));
        }
    };

    return (
        <div className="flex flex-col p-3">
            <div className="flex max-sm:flex-col items-center justify-between mb-4 gap-2">
                <h1 className="text-3xl font-semibold text-blue-600">Contratos</h1>
                <button type="button" onClick={handleRunExpirations} className={secondaryButton}
                    title="El proceso corre solo cada noche; esto lo ejecuta ya">
                    <span className="material-icons-outlined mr-2 text-base">update</span>
                    Ejecutar vencimientos ahora
                </button>
            </div>

            <form onSubmit={onFilter} className="flex flex-col md:flex-row items-center gap-3 mb-2">
                <div className="w-full md:flex-1 md:min-w-0">
                    <InputSearch placeholder="Email del usuario" id="user" name="user" {...register('user')} />
                </div>
                <div className="w-full md:flex-1 md:min-w-0">
                    <SelectStatus id="type" name="type" placeholder="Todos los tipos" options={TYPE_OPTIONS} {...register('type')} />
                </div>
                <div className="w-full md:flex-1 md:min-w-0">
                    <SelectStatus id="status" name="status" placeholder="Todos los estados" options={STATUS_OPTIONS} {...register('status')} />
                </div>
                <div className="w-full md:flex-1 md:min-w-0">
                    <SelectStatus id="expiringInDays" name="expiringInDays" placeholder="Cualquier vencimiento"
                        options={EXPIRING_OPTIONS} {...register('expiringInDays')} />
                </div>
                <div className="w-full md:w-auto md:shrink-0">
                    <ButtonFilter type="submit" />
                </div>
            </form>

            <hr className="my-2" />

            {error && <p className="text-red-600 mb-2">{error}</p>}
            {loading ? (
                <div className="text-center py-4">Cargando...</div>
            ) : (
                <ContractsTable contracts={data.content} sensors={sensors} onChanged={load}
                    emptyText="No hay contratos con esos filtros" />
            )}

            {!error && data.totalPages > 1 && (
                <CustomPaginator currentPage={page} totalPages={data.totalPages}
                    handlePageChange={(p) => { if (p >= 1 && p <= data.totalPages) setPage(p); }} />
            )}
        </div>
    );
};

export default ContractsView;
